import React, { useEffect } from 'react';
import {
  ListItemText,
  MenuItem,
  Checkbox,
  Select,
  FormControl,
  Input,
  MenuProps,
} from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { Background, Border, Text } from 'styles/themeColors';
import InfoIcon from 'components/UI/InfoIcon';
import styled from 'styled-components';
import { Size } from 'lib/styles';
import { Height } from 'styles/themeHeights';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import { PermissionCode } from 'data/UserPermissions';
import { useReadPermission } from 'hooks/useUserRoles';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';

const largeCheckboxSx = {
  '& .MuiSvgIcon-root': {
    fontSize: 28,
    padding: 0,
  },
  '&.MuiCheckbox-root': {
    color: Border.main,
    fontSize: 28,
    padding: 0
  },
};

const SelectElement = muiStyled(Select)(() => ({
  borderColor: Border.main,
  width: '100%',
  height: Height.m,
  background: Background.white,
  fontSize: Space.sp2x,
  borderRadius: BorderRadius.m,
  boxSizing: 'border-box',
  border: `1px solid ${Border.main}`,
  cursor: 'pointer',
  '&.Mui-disabled': {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  '& .MuiSelect-select': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  '& svg.MuiSvgIcon-root.MuiSelect-icon': {
    color: `${Text.primary} !important`,
  },
}));

const StyledInfoIcon = styled(InfoIcon)`
  display: flex;
  margin-left: ${Size.small};
  margin-top: 0.1rem;
`;

const PlaceHolder = styled.span`
  padding: 3px;
  color: #71777C;
`;

const MenuProp: Partial<MenuProps> = {
  disableAutoFocusItem: true,
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  PaperProps: {
    className: 'select-menu-parent',
  },
};

interface Props {
  dataList: { id: number | string; name: string, type?: string }[];
  filteredIds: (number | string)[];
  setFilterIds: (ids: (number | string)[]) => void;
  textLabel: string;
  inputWidth: string;
  initialAllSelected: boolean;
  className?: string;
  menuItemClassName?: string;
  tooltipText?: string;
  placeHolder?: string;
  disabled?: boolean;
  permissionCode?: PermissionCode;
}

export default function SelectionList({
  dataList,
  filteredIds,
  setFilterIds,
  textLabel,
  inputWidth,
  initialAllSelected,
  tooltipText = '',
  menuItemClassName = '',
  placeHolder = 'None',
  disabled = false,
  permissionCode
}: Props) {
  const { readOnly } = useReadPermission(permissionCode!);
  // Compute the render value string based on filteredIds
  const renderValue = dataList
    .filter((data) => filteredIds.includes(data.id))
    .map((data) => data.name)
    .join(', ');

  // useEffect to set initial filteredIds when dataList changes
  useEffect(() => {
    if (initialAllSelected) {
      const ids = dataList.map((data) => data.id);
      setFilterIds(ids);
      !dataList.length && setFilterIds([-1]);
    }
  }, [dataList.length, initialAllSelected]);

  // Function to filter out -1 ids and update the state
  const setFilteredIds = (ids: (number | string)[]) => {
    setFilterIds(ids.filter((id) => id !== -1));
  };

  // Handle selection change from the dropdown
  const handleSelectionChange = (event: { target: { value: unknown } }) => {
    const value = event.target.value as any;

    if (value.includes('all' as unknown)) {
      setFilteredIds(filteredIds.length === dataList.length ? [] : dataList.map((item) => item.id));
    } else {
      setFilteredIds(value);
    }
  };

  return (
    <div className="multiSelect-container">
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div>
          <span className="selection-label">{textLabel}</span>
        </div>
        <div>
          {tooltipText && <StyledInfoIcon
            productRedesignFlag
            tooltipText={tooltipText} // ToDo: Add tooltip text
            size={14}
          />
          }
        </div>
      </div>
      <ReadOnlyWrapper readOnly={readOnly}>
        <FormControl variant="standard" className="form-control" style={{ width: inputWidth, marginTop: 5, borderRadius: '6px !important' }}>
          <SelectElement
            labelId="selection-list-label"
            id="selection-list-checkbox"
            multiple
            value={filteredIds.length ? filteredIds : [-1]}
            onChange={handleSelectionChange}
            input={<Input style={{ width: "100%",cursor: readOnly ? 'not-allowed !important' : 'inherit' }} disabled={readOnly || disabled} />}
            renderValue={(selected: any) =>
              selected.includes(-1)
                ?
                <PlaceHolder>
                  <em>{placeHolder}</em>
                </PlaceHolder>
                : selected.length >= dataList.length
                  ? 'All'
                  : renderValue
            }
            MenuProps={dataList.length > 0 ? MenuProp : { PaperProps: { style: { display: 'none' } } }}
            style={{ padding: 8 }}
            disabled={readOnly || disabled}
          >
            <MenuItem key="All" value="all" className={menuItemClassName} style={{ minWidth: 200 }}>
              <ListItemText className="list-text" primary="All" />
              <Checkbox
                checked={filteredIds.length === dataList.length}
                sx={largeCheckboxSx}
                className="multi-select-checkbox"
              />
            </MenuItem>
            {dataList.length > 0 ? dataList.map((data) => (
              <MenuItem key={data.id} value={data.id} className={menuItemClassName} style={{ minWidth: 200 }}>
                <ListItemText className="list-text" primary={data.name} />
                <Checkbox
                  checked={filteredIds.includes(data.id)}
                  sx={largeCheckboxSx}
                  className="multi-select-checkbox"
                />
              </MenuItem>
            )) : null}
          </SelectElement>
        </FormControl>
      </ReadOnlyWrapper>
    </div>
  );
}
